import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { lockerReservationService, type CreateLockerReservationData } from '../../../shared/services/lockers/lockerReservationService';
import { useToast } from '../../../shared/hooks/useToast';
import { useAuth } from '../../auth/context/AuthContext';

interface LockerReservationFormValues {
  equipmentId: string;
  userId: string;
  durationHours: number;
  startDate: string;
  startTime: string;
  lockerId?: string;
}

interface LockerReservationPayload {
  equipmentId: string;
  userId: string;
  durationHours: number;
  startDate: string;
  startTime: string;
  tenantId: string;
  lockerId?: string;
  active?: boolean;
  status?: string;
  password?: string;
  reservation_date?: string;
  period?: number;
  maxPeriod?: number;
}

export function useLockerReservationForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);

  const createReservationMutation = useMutation({
    mutationFn: async (data: LockerReservationPayload) => {
      const response = await lockerReservationService.create(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locker-reservations'] });
      showToast('Reserva criada com sucesso!', 'success');
      navigate('/lockers');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Erro ao criar reserva';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  });

  const updateReservationMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<LockerReservationPayload> }) => {
      const response = await lockerReservationService.update(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locker-reservations'] });
      showToast('Reserva atualizada com sucesso!', 'success');
      navigate('/lockers');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Erro ao atualizar reserva';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  });

  const handleSubmit = async (values: LockerReservationFormValues) => {
    try {
      const payload: LockerReservationPayload = {
        ...values,
        active: true,
        period: values.durationHours * 60,
        maxPeriod: values.durationHours * 120,
        tenantId: auth.selectedTenant.id,
      };

      await createReservationMutation.mutateAsync(payload);
    } catch (error) {
      console.error('Erro ao submeter formulário:', error);
    }
  };

  const handleUpdate = async (id: string, values: LockerReservationFormValues) => {
    try {
      const payload: Partial<LockerReservationPayload> = {
        ...values,
        maxPeriod: values.durationHours * 60,
        tenantId: auth.selectedTenant.id,
      };

      await updateReservationMutation.mutateAsync({ id, data: payload });
    } catch (error) {
      console.error('Erro ao atualizar formulário:', error);
    }
  };

  return {
    handleSubmit,
    handleUpdate,
    isSubmitting: createReservationMutation.isPending || updateReservationMutation.isPending,
    error,
    setError,
  };
} 